import { useCallback } from 'react';
import createFastContext from './fastContext';
import { getAnalyticsParentPage, TRACK_EVENTS } from '@/providers/eventAnalytics/trackEvents';
import { useLocation } from 'react-router-dom';

type CurrentAppointmentData = {
  type: 'Phone' | 'Home';
  patientPhone?: string;
  address?: string;
  addressNumber?: string | null;
  podPhone?: string;
  appointmentDate: string;
};

type ContactDialogContent = {
  contactDialogMessagePlaceholder: string;
  isContactDialogBodyRequired: boolean;
  contactDialogTopic: string;
  contactDialogNote: string;
  isContactDialogVisible: boolean;
  buttonTitle: string;
  confirmPage: boolean;
  content: any;
  contactDialogTitle: string;
  options: string[];
  topicLabel: string;
  analyticsFormSubmittedEvent: string;
  analyticsParentPage?: string;
  currentAppointmentData?: CurrentAppointmentData;
};

const contactDialogDefaultContent: ContactDialogContent = {
  contactDialogMessagePlaceholder: 'Tell us more...',
  isContactDialogBodyRequired: true,
  contactDialogTopic: '',
  contactDialogNote: '',
  isContactDialogVisible: false,
  buttonTitle: 'Contact care team',
  confirmPage: false,
  content: null,
  contactDialogTitle: 'How can we help?',
  options: [''],
  topicLabel: 'Topic',
  analyticsFormSubmittedEvent: TRACK_EVENTS.CONTACT_CARE_TEAM_SENT,
  analyticsParentPage: getAnalyticsParentPage('/'),
};

const { Provider, useStore } = createFastContext({
  ...contactDialogDefaultContent,
});

export const ContactDialogProvider = Provider;

export const useContactDialogContext = () => {
  const location = useLocation();
  const [state, setState] = useStore((s) => s);

  const setContactDialogVisibility = useCallback(
    (isContactDialogVisible: boolean) => {
      setState({ isContactDialogVisible });
    },
    [setState]
  );

  const setContactDialogTopic = useCallback(
    (topic: string) => {
      setState({ contactDialogTopic: topic });
    },
    [setState]
  );

  const setContactDialogNote = useCallback(
    (contactDialogNote: string) => {
      setState({ contactDialogNote });
    },
    [setState]
  );

  const setContactDialogTitle = useCallback(
    (contactDialogTitle: string) => {
      setState({ contactDialogTitle });
    },
    [setState]
  );

  const setContactDialogMessagePlaceholder = useCallback(
    (contactDialogMessagePlaceholder: string) => {
      setState({ contactDialogMessagePlaceholder });
    },
    [setState]
  );

  const setIsContactDialogBodyRequired = useCallback(
    (isContactDialogBodyRequired: boolean) => {
      setState({ isContactDialogBodyRequired });
    },
    [setState]
  );

  const resetContactDialog = useCallback(() => {
    setState({
      ...contactDialogDefaultContent,
      contactDialogTitle: 'How can we help?',
      analyticsParentPage: getAnalyticsParentPage(location.pathname),
    });
  }, [location.pathname, setState]);

  const setButtonTitle = useCallback(
    (buttonTitle: string) => {
      setState({ buttonTitle });
    },
    [setState]
  );
  const setContent = useCallback(
    (contentData: any) => {
      setState({ content: contentData });
    },
    [setState]
  );

  const setConfirmPage = useCallback(
    (confirmPage: boolean) => {
      setState({ confirmPage });
    },
    [setState]
  );

  const setOptionsSelect = useCallback(
    (options: string[]) => {
      setState({ options });
    },
    [setState]
  );

  const setTopicLabel = useCallback(
    (topicLabel: string) => {
      setState({ topicLabel });
    },
    [setState]
  );

  const setAnalyticsFormSubmittedEvent = useCallback(
    (event: string) => {
      setState({ analyticsFormSubmittedEvent: event });
    },
    [setState]
  );

  const setAnalyticsParentPage = useCallback(
    (parentPage: string) => {
      setState({ analyticsParentPage: getAnalyticsParentPage(parentPage) });
    },
    [setState]
  );

  const setCurrentAppointmentData = useCallback(
    (currentAppointmentData: CurrentAppointmentData) => {
      setState({ currentAppointmentData });
    },
    [setState]
  );

  return {
    ...state,
    setContactDialogVisibility,
    setContactDialogTopic,
    setContactDialogNote,
    setContactDialogTitle,
    setContactDialogMessagePlaceholder,
    setIsContactDialogBodyRequired,
    resetContactDialog,
    setButtonTitle,
    setConfirmPage,
    setContent,
    setOptionsSelect,
    setTopicLabel,
    setAnalyticsFormSubmittedEvent,
    setAnalyticsParentPage,
    setCurrentAppointmentData,
  };
};
